import { format } from "date-fns";
import translations from "translations";

interface AxisRangeParams {
  dataMax: number;
  dataMin: number;
  maxPadding: number;
  tickCount: number;
}

interface AxisRange {
  interval: number;
  max: number;
  min: number;
}

/**
 * Calculate a "nice" interval that divides evenly and produces clean tick values.
 * Ensures the axis min/max are at most maxPadding away from the data min/max.
 * Returns { min, max, interval } where (max - min) / interval = tickCount
 */
function calculateNiceAxisRange({ dataMin, dataMax, tickCount, maxPadding }: AxisRangeParams): AxisRange {
  // Set axis bounds with maxPadding constraint
  const niceMin = dataMin - maxPadding;
  const niceMax = dataMax + maxPadding;

  // Calculate interval to ensure even spacing across the range
  const finalInterval = (niceMax - niceMin) / tickCount;

  return {
    interval: finalInterval,
    max: niceMax,
    min: niceMin,
  };
}

export function getEnvMeasurementsOption({
  dates,
  humidityData,
  temperatureData,
}: {
  dates: Date[];
  humidityData: Array<[Date, number]>;
  temperatureData: Array<[Date, number]>;
}) {
  const humiditySorted = [...(humidityData ?? [])?.map(([_, value]) => value)].sort((a, b) => a - b);
  const humidityRange = calculateNiceAxisRange({
    dataMin: humiditySorted?.length ? humiditySorted[0] : 0,
    dataMax: humiditySorted?.length ? humiditySorted[humiditySorted.length - 1] : 100,
    maxPadding: 2,
    tickCount: 5,
  });

  const temperatureSorted = [...(temperatureData ?? [])?.map(([_, value]) => value)].sort((a, b) => a - b);
  const temperatureRange = calculateNiceAxisRange({
    dataMin: temperatureSorted?.length ? temperatureSorted[0] : 0,
    dataMax: temperatureSorted?.length ? temperatureSorted[temperatureSorted.length - 1] : 100,
    maxPadding: 2,
    tickCount: 5,
  });

  return {
    color: ["#4C79CF", "#CF5C4C"],
    legend: {
      left: 115,
      bottom: 10,
      textStyle: {
        color: "rgba(0, 0, 0, 0.88)",
        fontWeight: "400",
        fontSize: "12px",
        fontFamily: "var(--copytext-font-family)",
      },
      itemGap: 40,
      data: [
        {
          name: translations.entities.capability.capabilityNames.Humidity,
          icon: "circle",
        },
        { name: translations.entities.capability.capabilityNames.Temperature, icon: "circle" },
      ],
    },

    /**
     *
     *
     */
    series: [
      {
        name: translations.entities.capability.capabilityNames.Humidity,
        type: "line",
        data: humidityData,
        showSymbol: true,
        symbol: "circle",
        symbolSize: 6,
      },
      {
        name: translations.entities.capability.capabilityNames.Temperature,
        type: "line",
        yAxisIndex: 1,
        data: temperatureData,
        showSymbol: true,
        symbol: "circle",
        symbolSize: 6,
      },
    ],
    tooltip: {
      formatter(el: any) {
        let humidityInfo;
        let temperatureInfo;

        el?.forEach((item: any) => {
          if (item?.seriesName?.includes?.("Humidity") && item?.data?.[1] !== undefined) {
            humidityInfo = `${item?.seriesName ?? ""} &nbsp;&nbsp;<b>${Number(item?.data?.[1]).toFixed(1)}%</b>`;
          }
          if (item?.seriesName?.includes?.("Temperature") && item?.data?.[1] !== undefined) {
            temperatureInfo = `${item?.seriesName ?? ""}&nbsp;&nbsp; <b>${Number(item?.data?.[1]).toFixed(1)}°C</b>`;
          }
        });

        return `<div class="chart-tooltip-item">
      <div style="margin-bottom: 8px;"><b>${format(new Date(el[0].data[0]), "dd/M/yyyy, HH:mm")}</b></div>
      ${
        humidityInfo
          ? `<div class="chart-tooltip-item-row" style="display: flex; flex-direction: row; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 4px;">
              <div style="display: flex; align-items: center;">
                <div class="chart-humidity-legend-dot"></div>
                <div class="chart-tooltip-title">${humidityInfo}</div>
              </div>
            </div>`
          : ""
      }
      ${
        temperatureInfo
          ? `<div class="chart-tooltip-item-row" style="display: flex; flex-direction: row; justify-content: space-between; align-items: center; gap: 10px;">
              <div style="display: flex; align-items: center;">
                <div class="chart-temp-legend-dot"></div>
                <div class="chart-tooltip-title">${temperatureInfo}</div>
              </div>
            </div>`
          : ""
      }
    </div>`;
      },
      textStyle: { fontFamily: "var(--copytext-font-family)", fontSize: "10px", color: "rgba(0, 0, 0, 0.56)" },
      trigger: "axis",
      axisPointer: {
        type: "none",
      },
    },
    xAxis: [
      {
        type: "time",
        axisTick: {
          show: false,
        },
        data: dates,
        axisLine: { show: false },
        axisLabel: {
          fontFamily: "var(--copytext-font-family)",
          fontSize: "14px",
          color: "rgba(0, 0, 0, 0.56)",
          margin: 15,
          interval: 2,
          formatter: (value: any) => {
            return format(new Date(value), "d MMM, HH:mm");
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: `${translations.entities.capability.capabilityNames.Humidity} (%)`,
        nameTextStyle: {
          fontFamily: "var(--copytext-font-family)",
          fontSize: "10px",
          align: "center",
          color: "rgba(0, 0, 0, 0.56)",
        },
        min: humidityRange.min,
        max: humidityRange.max,
        position: "left",
        interval: humidityRange.interval,
        axisLabel: {
          fontFamily: "var(--copytext-font-family)",
          fontSize: "12px",
          color: "rgba(0, 0, 0, 0.56)",
          margin: 30,
          align: "left",
          formatter: (value: number) => value.toFixed(1),
        },
      },
      {
        type: "value",
        name: `${translations.entities.capability.capabilityNames.Temperature} (°C)`,
        nameTextStyle: {
          fontFamily: "var(--copytext-font-family)",
          fontSize: "10px",
          color: "rgba(0, 0, 0, 0.56)",
          align: "center",
        },
        min: temperatureRange.min,
        max: temperatureRange.max,
        position: "right",
        interval: temperatureRange.interval,
        axisLabel: {
          align: "right",
          fontFamily: "var(--copytext-font-family)",
          fontSize: "12px",
          color: "rgba(0, 0, 0, 0.56)",
          margin: 35,
          formatter: (value: number) => value.toFixed(1),
        },
        axisLine: {
          show: false,
        },
      },
    ],
  };
}
